import { catalogNameToFhir, getTRPCClient, syncKey, TREATMENT_DEFINITION_KIND } from "@repo/api";
import {
  type ChangeTreatmentDefinitionAvailabilityInput,
  type CorrectTreatmentDefinitionInput,
  changeTreatmentDefinitionAvailabilityInputSchema,
  correctTreatmentDefinitionInputSchema,
  type DefineTreatmentDefinitionInput,
  defineTreatmentDefinitionInputSchema,
  type ReviseTreatmentDefinitionInput,
  reviseTreatmentDefinitionInputSchema,
} from "@repo/api/schemas";
import type { StandardObservationKey, StandardParameterReference } from "@repo/domain";
import { mapAuthored, mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, ilike, inArray, max, or, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import type { CatalogValueDto } from "../fhir/catalog-value";
import {
  type TreatmentDefinitionRowContent,
  type TreatmentDefinitionRowSpine,
  writeTreatmentDefinitionRow,
} from "../fhir/treatment-definition-storage";
import type { ActivityDefinitionRow } from "../fhir/types";
import { projectTreatmentDefinitionRow } from "./treatment-definition-projection";

const ACTIVITY_DEFINITION_COLLECTION = "activityDefinition";
/** Every treatment canonical this app mints sits directly under this base. */
export const TREATMENT_DEFINITION_CANONICAL_BASE = canonicalBase("ActivityDefinition");

export type TreatmentDefinitionAvailability = "Active" | "Inactive";

export type TreatmentValueDefinitionDto = CatalogValueDto;

export interface TreatmentDefinitionDto {
  url: string;
  /** Backing ActivityDefinition identity required by legacy Protocol writes. */
  resourceId?: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: TreatmentDefinitionAvailability;
  procedureNoteLabel?: string;
  effectiveParameters: readonly Exclude<TreatmentDefinitionDto["parameters"][number], StandardParameterReference>[];
  standardObservations: readonly StandardObservationKey[];
  chartedObservations: TreatmentDefinitionDto["observations"];
  observations: readonly {
    id: string;
    label: string;
    repeatable: boolean;
    noteLabel?: string;
    components: readonly { id: string; label: string; value: TreatmentValueDefinitionDto }[];
  }[];
  parameters: readonly (
    | { id: string; label: string; value: TreatmentValueDefinitionDto }
    | StandardParameterReference
  )[];
  deviceRequirements: readonly { id: string; definition: string }[];
  locationRequirement?: string;
}

export interface TreatmentDefinitionFilters {
  availability?: TreatmentDefinitionAvailability;
  search?: string;
  /** Exact immutable revisions required by an aggregate read model. */
  canonicals?: readonly string[];
}

const activityDefinitions = () =>
  getDataStoreClient().getCollection<ActivityDefinitionRow, string>(ACTIVITY_DEFINITION_COLLECTION);

export function mintTreatmentDefinitionUrl(): string {
  return canonicalUrl("ActivityDefinition", mintEntityId());
}

export function useTreatmentDefinition(url: string | undefined): TreatmentDefinitionDto | undefined {
  const collection = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "treatment-definition", url ?? null],
    query: (query) => {
      if (!url) return undefined;
      return (
        query
          .from({ definition: collection })
          .where(({ definition }) =>
            and(eq(definition.catalogModelKind, TREATMENT_DEFINITION_KIND), eq(definition.url, url)),
          )
          .orderBy(({ definition }) => definition.catalogVersion, "desc")
          // FHIR nested extensions have no declarative query expression.
          .fn.select(({ definition }) => projectTreatmentDefinitionRow(definition))
          .findOne()
      );
    },
  });
  return result.data;
}

export interface TreatmentDefinitionsResult {
  definitions: TreatmentDefinitionDto[];
  isLoading: boolean;
  isReady: boolean;
}

export function useTreatmentDefinitionsResult(filters: TreatmentDefinitionFilters = {}): TreatmentDefinitionsResult {
  const collection = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const canonicals = filters.canonicals;
  const result = useLiveQuery({
    queryKey: [
      collection.id,
      "treatment-definitions",
      status ?? null,
      search ?? null,
      canonicals?.length ?? -1,
      ...(canonicals ?? []),
    ],
    query: (query) => {
      if (canonicals) {
        if (canonicals.length === 0) return undefined;
        let exact = query
          .from({ definition: collection })
          .where(({ definition }) => eq(definition.catalogModelKind, TREATMENT_DEFINITION_KIND))
          .where(({ definition }) =>
            canonicals.length === 1
              ? eq(definition.definitionCanonical, canonicals[0] as string)
              : inArray(definition.definitionCanonical, [...canonicals]),
          );
        if (status) exact = exact.where(({ definition }) => eq(definition.status, status));
        if (search) {
          exact = exact.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          );
        }
        return exact
          .orderBy(({ definition }) => definition.title, "asc")
          .select(({ definition }) => ({ ...definition }));
      }
      const latestVersions = query
        .from({ candidate: collection })
        .where(({ candidate }) => eq(candidate.catalogModelKind, TREATMENT_DEFINITION_KIND))
        .groupBy(({ candidate }) => candidate.url)
        .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
      const current = query
        .from({ definition: collection })
        .join({ latest: latestVersions }, ({ definition, latest }) => eq(definition.url, latest.url))
        .where(({ definition, latest }) =>
          and(
            eq(definition.catalogModelKind, TREATMENT_DEFINITION_KIND),
            eq(definition.catalogVersion, latest.version),
          ),
        );
      const available = status ? current.where(({ definition }) => eq(definition.status, status)) : current;
      const matching = search
        ? available.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          )
        : available;
      return matching
        .orderBy(({ definition }) => definition.title, "asc")
        .select(({ definition }) => ({ ...definition }));
    },
  });
  const definitions = ((result.data as ActivityDefinitionRow[] | undefined) ?? []).map(projectTreatmentDefinitionRow);
  return { definitions, isLoading: result.isLoading, isReady: result.isReady };
}

export function useTreatmentDefinitions(filters: TreatmentDefinitionFilters = {}): TreatmentDefinitionDto[] {
  return useTreatmentDefinitionsResult(filters).definitions;
}

type TreatmentContent = Pick<
  DefineTreatmentDefinitionInput,
  "procedureNoteLabel" | "standardObservations" | "observations" | "parameters" | "requirements"
>;

function memberId(id: string | undefined, prefix: string, index: number): string {
  return id ?? `${prefix}-${index + 1}`;
}

/**
 * The authored content with every member id settled, which is the whole of
 * what the tRPC input leaves open: the server mints the same sequences, so
 * writing them here is what lets the optimistic row read back unchanged.
 */
function withMemberIds(content: TreatmentContent): TreatmentDefinitionRowContent {
  let componentIndex = 0;
  let optionIndex = 0;
  const value = (input: TreatmentContent["observations"][number]["components"][number]["value"]): CatalogValueDto =>
    input.kind === "Choice"
      ? {
          ...input,
          options: input.options.map((option) => ({
            id: memberId(option.id, "opt", optionIndex++),
            label: option.label,
          })),
        }
      : { ...input };
  return {
    ...(content.procedureNoteLabel === undefined ? {} : { procedureNoteLabel: content.procedureNoteLabel }),
    // The standards are never authored members and never reach storage: the
    // reader derives them back from the opt-outs the writer stamps.
    standardObservations: content.standardObservations,
    observations: content.observations.map((observation, observationIndex) => ({
      id: memberId(observation.id, "obs", observationIndex),
      label: observation.label,
      repeatable: observation.repeatable,
      ...(observation.noteLabel === undefined ? {} : { noteLabel: observation.noteLabel }),
      components: observation.components.map((component) => ({
        id: memberId(component.id, "cmp", componentIndex++),
        label: component.label,
        value: value(component.value),
      })),
    })),
    parameters: mapAuthored(content.parameters, (parameter, parameterIndex) => ({
      id: memberId(parameter.id, "prm", parameterIndex),
      label: parameter.label,
      value: value(parameter.value),
    })),
    deviceRequirements: content.requirements.devices.map((requirement, requirementIndex) => ({
      id: memberId(requirement.id, "dev", requirementIndex),
      definition: requirement.definition,
    })),
    ...(content.requirements.location === undefined ? {} : { locationRequirement: content.requirements.location }),
  };
}

function optimisticId(url: string, version: number): string {
  let hash = 2166136261;
  for (const character of url) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `optimistic-treatment-${(hash >>> 0).toString(36)}-${version}`;
}

/**
 * A REAL `ActivityDefinition`, in the shape the engine's encoder writes, plus
 * the derived columns an insert bypasses (`deriveFields` runs at sync only).
 * The projection reads it exactly as it reads the synced row that replaces it.
 */
function optimisticRow(spine: TreatmentDefinitionRowSpine, content: TreatmentContent): ActivityDefinitionRow {
  return {
    ...writeTreatmentDefinitionRow(spine, withMemberIds(content)),
    id: optimisticId(spine.url, spine.version),
    catalogModelKind: TREATMENT_DEFINITION_KIND,
    catalogVersion: spine.version,
    definitionCanonical: `${spine.url}|${spine.version}`,
    catalogViewScope: "catalog-definitions",
  };
}

function currentRow(url: string): ActivityDefinitionRow & { id: string } {
  const row = [...activityDefinitions().state.values()]
    .filter((candidate) => candidate.url === url && candidate.catalogModelKind === TREATMENT_DEFINITION_KIND)
    .sort((left, right) => right.catalogVersion - left.catalogVersion)[0];
  if (!row?.id) throw new Error(`Treatment Definition '${url}' is not available in the data store`);
  return { ...row, id: row.id };
}

export const defineTreatmentDefinition = defineWrite<DefineTreatmentDefinitionInput>({
  optimistic: (params) => {
    const input = defineTreatmentDefinitionInputSchema.parse(params);
    activityDefinitions().insert(
      optimisticRow(
        {
          url: input.definitionUrl,
          version: 1,
          mintedAt: new Date().toISOString(),
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          availability: "Active",
        },
        input,
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.treatmentDefinition.define.mutate(input, options);
  },
});

export const reviseTreatmentDefinition = defineWrite<ReviseTreatmentDefinitionInput>({
  optimistic: (params) => {
    const input = reviseTreatmentDefinitionInputSchema.parse(params);
    const current = projectTreatmentDefinitionRow(currentRow(input.definitionUrl));
    activityDefinitions().insert(
      optimisticRow(
        {
          url: current.url,
          version: input.expectedVersion + 1,
          mintedAt: new Date().toISOString(),
          name: current.name,
          ...(current.description === undefined ? {} : { description: current.description }),
          availability: current.availability,
        },
        input,
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.treatmentDefinition.revise.mutate(input, options);
  },
});

export const correctTreatmentDefinition = defineWrite<CorrectTreatmentDefinitionInput>({
  optimistic: (params) => {
    const input = correctTreatmentDefinitionInputSchema.parse(params);
    const row = currentRow(input.definitionUrl);
    activityDefinitions().update(syncKey(row), (draft) => {
      if (input.name !== undefined) Object.assign(draft, catalogNameToFhir(input.name));
      if (input.description === null) delete draft.description;
      else if (input.description !== undefined) draft.description = input.description;
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.treatmentDefinition.correct.mutate(input, options);
  },
});

function availabilityAction(availability: TreatmentDefinitionAvailability) {
  return defineWrite<ChangeTreatmentDefinitionAvailabilityInput>({
    optimistic: (params) => {
      const input = changeTreatmentDefinitionAvailabilityInputSchema.parse(params);
      const row = currentRow(input.definitionUrl);
      activityDefinitions().update(syncKey(row), (draft) => {
        draft.status = availability === "Active" ? "active" : "retired";
      });
    },
    persist: async (input, options) => {
      const mutation =
        availability === "Active"
          ? getTRPCClient().catalog.treatmentDefinition.activate
          : getTRPCClient().catalog.treatmentDefinition.deactivate;
      await mutation.mutate(input, options);
    },
  });
}

export const activateTreatmentDefinition = availabilityAction("Active");
export const deactivateTreatmentDefinition = availabilityAction("Inactive");
